import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useOrgHall } from "@/hooks/useOrgHall";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Info, Settings, Activity, Minus, Plus, Ban } from "lucide-react";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";
import { resolveMoney } from "@/utils/formatPrice";

import { BookingDetailInfo } from "./detail/BookingDetailInfo";
import { BookingDetailPayments } from "./detail/BookingDetailPayments";
import { BookingDetailActions } from "./detail/BookingDetailActions";
import { BookingDetailLog } from "./detail/BookingDetailLog";
import type {
  BookingDetailModalProps,
  BookingDetail,
  BookingPlayerWithProfile,
  AddonSelection,
  ActivityLogEntry,
  PlayerDisplayInfo,
} from "./detail/types";

export default function BookingDetailModal({ open, onOpenChange, bookingId, onMoveBooking }: BookingDetailModalProps) {
  const { t } = useTranslation("booking");
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const { courts: orgCourts } = useOrgHall();
  const { paymentMethodOptions: paymentMethods } = usePaymentMethods();
  const qc = useQueryClient();

  const [confirmingUnblock, setConfirmingUnblock] = useState(false);

  const [settingsForm, setSettingsForm] = useState<{
    custom_cancellation_hours: number | null;
    age_group: string;
    info_text: string;
  } | null>(null);

  const { data: booking } = useQuery({
    queryKey: ["booking-detail", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        // UPPDRAG-36: extend the creator embed with the profile→customers
        // FK so "Bokad av" can render as a clickable PlayerNameButton
        // (navigates to /manager/customers/:customerId for the creator).
        .select("*, courts(name, court_type, halls(name)), profiles:bookings_user_id_profiles_fkey(id, full_name, level, phone), creator:bookings_created_by_profiles_fkey(id, full_name, customers(id)), customers(id, full_name, email, phone, customer_categories(name))")
        .eq("id", bookingId)
        .single();
      if (error) throw error;
      const result = data as unknown as BookingDetail;
      setSettingsForm({
        custom_cancellation_hours: result.custom_cancellation_hours ?? null,
        age_group: result.age_group ?? "all",
        info_text: result.info_text ?? "",
      });
      return result;
    },
    enabled: open && !!bookingId,
  });

  const { data: bookerEmail } = useQuery({
    queryKey: ["booker-email", booking?.user_id, (booking as any)?.customer_id],
    queryFn: async () => {
      // Prefer customer email (direct FK) over profile-based lookup
      if ((booking as any)?.customer_id) {
        const { data: cust } = await supabase
          .from("customers")
          .select("email")
          .eq("id", (booking as any).customer_id)
          .maybeSingle();
        if (cust?.email) return cust.email;
      }
      const { data } = await supabase
        .from("customers")
        .select("email")
        .eq("user_id", booking!.user_id)
        .maybeSingle();
      return data?.email ?? null;
    },
    enabled: !!booking?.user_id,
  });

  const { data: players = [] } = useQuery({
    queryKey: ["booking-players", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_players")
        // UPPDRAG-36: extend with profile→customers embed so each row in
        // the Spelare list can render as a clickable PlayerNameButton.
        // G-052: + customer_categories so the list shows category badges
        // (parity with the grid's BookingDetailPanel).
        .select("*, profiles:booking_players_user_id_fkey(id, full_name, level, customers(id, customer_categories(name)))")
        .eq("booking_id", bookingId);
      if (error) throw error;
      return (data ?? []) as unknown as BookingPlayerWithProfile[];
    },
    enabled: open && !!bookingId,
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["booking-payments-detail", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_payments")
        .select("*")
        .eq("booking_id", bookingId);
      if (error) throw error;
      return (data ?? []) as Tables<"booking_payments">[];
    },
    enabled: open && !!bookingId,
  });

  const { data: addonSelections = [] } = useQuery({
    queryKey: ["booking-addon-selections", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_addon_selections" as any)
        .select("*, addon:booking_addons(*)")
        .eq("booking_id", bookingId);
      if (error) throw error;
      return (data as AddonSelection[] | null) ?? [];
    },
    enabled: open && !!bookingId,
  });

  const { data: rawActivityLog = [] } = useQuery({
    queryKey: ["booking-activity-log", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_activity_log" as any)
        .select("*")
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ActivityLogEntry[];
    },
    enabled: open && !!bookingId,
  });

  // Gap 6: Fetch move history and merge into activity log
  const { data: moveHistory = [] } = useQuery({
    queryKey: ["booking-moves", bookingId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_moves" as any)
        .select("id, from_court_id, to_court_id, from_start_time, to_start_time, reason, price_old, price_new, price_action, created_at, courts:to_court_id(name)")
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: true });
      if (error) return [];
      return (data ?? []) as any[];
    },
    enabled: open && !!bookingId,
  });

  const activityLog = useMemo(() => {
    const moveEntries: ActivityLogEntry[] = moveHistory.map((m: any) => ({
      id: m.id,
      action: "booking_moved",
      actor_type: "admin",
      description: `Flyttad till ${m.courts?.name ?? "annan bana"} ${m.to_start_time ? format(new Date(m.to_start_time), "HH:mm") : ""}${m.reason ? ` — ${m.reason}` : ""}${m.price_action && m.price_action !== "same" ? ` (pris: ${m.price_old} → ${m.price_new} kr)` : ""}`,
      created_at: m.created_at,
    }));
    return [...rawActivityLog, ...moveEntries].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
  }, [rawActivityLog, moveHistory]);

  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      if (!settingsForm) return;
      const { error } = await supabase
        .from("bookings")
        .update({
          custom_cancellation_hours: settingsForm.custom_cancellation_hours,
          age_group: settingsForm.age_group,
          info_text: settingsForm.info_text || null,
        } as any)
        .eq("id", bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking-detail", bookingId] });
      toast.success(t("toast.settingsSaved"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte spara"); },
  });

  const unblockMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("bookings")
        .delete()
        .eq("id", bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking-detail", bookingId] });
      qc.invalidateQueries({ queryKey: ["schedule-bookings"] });
      toast.success(t("unblockSuccess"));
      onOpenChange(false);
    },
    onError: (err: Error) => {
      console.error(err);
      toast.error(err?.message || t("somethingWentWrong"));
    },
  });

  const filteredPaymentMethods = paymentMethods.filter(
    (m) => m.value !== "unpaid" && m.value !== "link"
  );

  if (!booking) return null;

  // Sprint D aggregates: prefer amount_minor; both totals in öre.
  const totalAmountMinor = payments.reduce(
    (sum: number, p) => sum + (resolveMoney((p as any).amount_minor, p.amount, (p as any).currency)?.valueMinor ?? 0), 0);
  const paidAmountMinor = payments
    .filter((p) => ["paid", "manual"].includes(p.status))
    .reduce((sum: number, p) => sum + (resolveMoney((p as any).amount_minor, p.amount, (p as any).currency)?.valueMinor ?? 0), 0);
  const totalAmount = Math.round(totalAmountMinor / 100);
  const paidAmount = Math.round(paidAmountMinor / 100);

  const customerData = (booking as any).customers;
  const bookerName = customerData?.full_name || booking.profiles?.full_name || "Bokare";
  const bookerPhone = customerData?.phone || booking.profiles?.phone || null;

  // UPPDRAG-36: customerId for the booker comes from the FK-direct
  // bookings.customers join (always the most accurate — direct row, not
  // a derived lookup). For each booking_players row, customerId comes
  // from the profile→customers embed we added to the SELECT above.
  const allPlayers: PlayerDisplayInfo[] = [
    {
      id: booking.profiles?.id ?? "",
      name: bookerName,
      level: booking.profiles?.level ?? null,
      phone: bookerPhone,
      isBooker: true,
      checkedInAt: booking.checked_in_at,
      customerId: customerData?.id ?? null,
      categoryLabel: customerData?.customer_categories?.name ?? null,
    },
    ...players.map((p): PlayerDisplayInfo => ({
      id: p.user_id,
      name: p.profiles?.full_name ?? "Spelare",
      level: p.profiles?.level ?? null,
      phone: null,
      isBooker: false,
      checkedInAt: p.checked_in_at,
      customerId: p.profiles?.customers?.[0]?.id ?? null,
      categoryLabel: (p.profiles?.customers?.[0] as any)?.customer_categories?.name ?? null,
    })),
  ];

  const courtName = booking.courts?.name ?? "";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[480px] sm:max-w-[480px] p-0 flex flex-col">
        <SheetHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2">
          <SheetTitle>Bokning — {courtName}</SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="info" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-4 sm:mx-6 w-auto">
            <TabsTrigger value="info" className="flex-1 gap-1">
              <Info className="h-3.5 w-3.5" />
              Info
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="settings" className="flex-1 gap-1">
                <Settings className="h-3.5 w-3.5" />
                Inställningar
              </TabsTrigger>
            )}
            <TabsTrigger value="activity" className="flex-1 gap-1">
              <Activity className="h-3.5 w-3.5" />
              Aktivitet
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 min-h-0">
            {/* INFO TAB */}
            <TabsContent value="info" className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-5 mt-4">
              {/* Blocked banner + unblock (skip for activity bookings) */}
              {booking.status === "blocked" && isAdmin && !booking.activity_id && (
                <div className="rounded-lg bg-muted border border-muted-foreground/30 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Ban className="h-4 w-4 text-muted-foreground" />
                    {t("admin:scheduleGrid.blockedByAdmin")}
                  </div>
                  {booking.info_text && (
                    <p className="text-xs text-muted-foreground">{booking.info_text}</p>
                  )}
                  {!confirmingUnblock ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmingUnblock(true)}
                    >
                      {t("unblock")}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">{t("unblockConfirm")}</p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={unblockMutation.isPending}
                          onClick={() => unblockMutation.mutate()}
                        >
                          {t("unblock")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmingUnblock(false)}
                        >
                          {t("backLabel")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <BookingDetailInfo
                booking={booking}
                bookerEmail={bookerEmail ?? null}
                addonSelections={addonSelections}
                /* UPPDRAG-36: close the sheet when the user navigates to a
                   customer card so the new route isn't covered by the open overlay. */
                onAfterNavigate={() => onOpenChange(false)}
              />

              <BookingDetailPayments
                booking={booking}
                bookingId={bookingId}
                allPlayers={allPlayers}
                payments={payments}
                filteredPaymentMethods={filteredPaymentMethods}
                /* UPPDRAG-36: same — close on player → customer-card navigate. */
                onAfterNavigate={() => onOpenChange(false)}
              />

              <BookingDetailActions
                booking={booking}
                courtName={courtName}
                allPlayers={allPlayers}
                payments={payments}
                totalAmount={totalAmount}
                paidAmount={paidAmount}
                bookerEmail={bookerEmail ?? null}
                onMoveBooking={onMoveBooking}
                onCancelled={() => onOpenChange(false)}
              />
            </TabsContent>

            {/* SETTINGS TAB (admin only) */}
            {isAdmin && (
              <TabsContent value="settings" className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-5 mt-4">
                {settingsForm && (
                  <>
                    <div className="space-y-2">
                      <Label>Avbokningstid (timmar)</Label>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setSettingsForm(f => f ? { ...f, custom_cancellation_hours: Math.max(0, (f.custom_cancellation_hours ?? 24) - 1) } : f)}
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </Button>
                        <NumberInput
                          className="w-20 h-8 text-center"
                          value={settingsForm.custom_cancellation_hours ?? undefined}
                          onValueChange={(v) => setSettingsForm(f => f ? { ...f, custom_cancellation_hours: v || null } : f)}
                          placeholder="Standard"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setSettingsForm(f => f ? { ...f, custom_cancellation_hours: (f.custom_cancellation_hours ?? 24) + 1 } : f)}
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                        <span className="text-xs text-muted-foreground">Lämna tomt för hallens standard</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Åldersgrupp</Label>
                      <Select
                        value={settingsForm.age_group}
                        onValueChange={(v) => setSettingsForm(f => f ? { ...f, age_group: v } : f)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alla</SelectItem>
                          <SelectItem value="adults">Vuxna</SelectItem>
                          <SelectItem value="juniors">Juniorer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Informationstext</Label>
                      <Textarea
                        value={settingsForm.info_text}
                        onChange={(e) => setSettingsForm(f => f ? { ...f, info_text: e.target.value } : f)}
                        placeholder="Synlig för spelarna på bokningsdetaljen..."
                        rows={3}
                      />
                    </div>

                    <Button
                      className="w-full"
                      onClick={() => saveSettingsMutation.mutate()}
                      disabled={saveSettingsMutation.isPending}
                    >
                      Spara inställningar
                    </Button>
                  </>
                )}
              </TabsContent>
            )}

            {/* ACTIVITY TAB */}
            <TabsContent value="activity" className="px-4 sm:px-6 pb-4 sm:pb-6 mt-4">
              <BookingDetailLog activityLog={activityLog} />
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
